import moment from 'moment/moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { formatCurrency } from '../../../../common-utils/formatters';
import { ROUTES } from '../../../utils/routes';
import { DatePicker } from '../../common/DatePickerCompat';
import Table from '../../common/MUITable';
import { showToast } from '../../common/Notification/ToastrNotif.module';
import RedirectionCoproButtons from '../../common/RedirectionCoproButtons';
import RedirectionMapButtons from '../../common/RedirectionMapButtons';

export type PresenceStatus = 'confirmed' | 'declined' | 'pending';

export type CoproRow = {
  id: number;
  companyId: number;
  remoteId: string;
  name: string;
  address: string;
  cp: string;
  city: string;
  gestionnaire: string;
  nbLots: number | null;
  salesAmount: number | null;
  salesCommission: number | null;
  nbSales: number | null;
  m2ForNbSales: number | null;
  salesAvgForM2: number | null;
  meetingDate: string | null; // yyyy-MM-dd
  apiMeetingDate: string | null; // yyyy-MM-dd
  meetingDates?: string[]; // normalized yyyy-MM-dd array (0..2)
  lastAgDate?: string | null; // yyyy-MM-dd
  nextAgForecast?: string | null; // yyyy-MM
  boitageQualification?: string | null;
  presence?: PresenceStatus; // for current meetingDate
};

type BlurDatePickerProps = {
  value: string | null;
  onSave: (iso: string) => void;
  onClear?: () => void;
  minDate?: moment.Moment;
  maxDate?: moment.Moment;
  showClearAction?: boolean;
  errorMessageMin?: string;
  errorMessageMax?: string;
};

function BlurDatePicker({ value, onSave, onClear, minDate, maxDate, showClearAction, errorMessageMin, errorMessageMax }: BlurDatePickerProps) {
  const [localValue, setLocalValue] = useState<moment.Moment | null>(value ? moment(value, 'YYYY-MM-DD') : null);
  const savedValueRef = useRef<string | null>(value);
  const isPickerOpenRef = useRef(false);

  useEffect(() => {
    setLocalValue(value ? moment(value, 'YYYY-MM-DD') : null);
    savedValueRef.current = value;
  }, [value]);

  const isReasonableDate = (m: moment.Moment | null): boolean => {
    if (!m || !m.isValid()) { return false; }
    const year = m.year();
    return year >= 1900 && year <= 2100;
  };

  const revert = useCallback(() => {
    setLocalValue(savedValueRef.current ? moment(savedValueRef.current, 'YYYY-MM-DD') : null);
  }, []);

  const doSave = useCallback((m: moment.Moment | null) => {
    if (!isReasonableDate(m)) { return; }
    const iso = m!.format('YYYY-MM-DD');
    if (iso === savedValueRef.current) { return; }
    if (minDate && m!.isBefore(minDate, 'day')) {
      showToast('error', errorMessageMin || 'La date sélectionnée est trop ancienne');
      revert();
      return;
    }
    if (maxDate && m!.isAfter(maxDate, 'day')) {
      showToast('error', errorMessageMax || 'La date sélectionnée est trop récente');
      revert();
      return;
    }
    onSave(iso);
  }, [onSave, minDate, maxDate, errorMessageMin, errorMessageMax, revert]);

  return (
    <DatePicker
      value={localValue}
      onChange={(newValue) => {
        if (!newValue && onClear) {
          onClear();
          return;
        }
        setLocalValue(newValue);
      }}
      onOpen={() => { isPickerOpenRef.current = true; }}
      onClose={() => { isPickerOpenRef.current = false; }}
      onAccept={(newValue) => {
        if (!newValue && onClear) { onClear(); return; }
        doSave(newValue);
      }}
      format="DD/MM/YYYY"
      views={['day']}
      slotProps={{
        textField: {
          size: 'small',
          onBlur: () => {
            if (!isPickerOpenRef.current) {
              doSave(localValue);
            }
          },
        },
        ...(showClearAction ? { actionBar: { actions: ['clear', 'cancel', 'accept'] as const } } : {}),
      }}
      {...(showClearAction ? { localeText: { clearButtonLabel: 'Effacer' } } : {})}
      {...(minDate ? { minDate } : {})}
      {...(maxDate ? { maxDate } : {})}
    />
  );
}

interface GeneralAssemblyTableProps {
  rows: CoproRow[];
  isEditable: (row: CoproRow) => boolean;
  onOpenPresence: (row: CoproRow) => void;
  onMeetingDateChange: (coproId: number, isoDate: string, previousPlanned: string | null, lastAgDate: string | null) => void;
  onMeetingDateClear: (coproId: number, previousPlanned: string | null, lastAgDate: string | null) => void;
  onLastAgDateChange: (coproId: number, isoDate: string, plannedDate: string | null) => void;
}

const headers = [
  { title: 'N° COPRO', class: '' },
  { title: 'NOM IMMEUBLE', class: '' },
  { title: 'ADRESSE IMMEUBLE', class: '' },
  { title: 'MONTANT DES VENTES', class: '', sortable: true },
  { title: 'MONTANT COMMISSION', class: '', sortable: true },
  { title: 'NB LOTS PRINCIPAUX', class: '', sortable: true },
  { title: 'NB VENTE', class: '', sortable: true },
  { title: 'M² MOY/VENTE', class: '', sortable: true },
  { title: 'PRIX MOYEN M²', class: '', sortable: true },
  { title: 'DERNIÈRE AG', class: '', sortable: true },
  { title: 'DATE AG PRÉVUE', class: '', sortable: true },
  { title: 'GESTIONNAIRE', class: '' },
  { title: 'PRÉSENCE', class: '' },
  { title: 'ACTIONS', class: '' },
];

const GeneralAssemblyTable: React.FC<GeneralAssemblyTableProps> = ({ rows, isEditable, onOpenPresence, onMeetingDateChange, onMeetingDateClear, onLastAgDateChange }) => {
  const navigate = useNavigate();
  const data = rows.map((r) => {
    const address = `${r.address}\n${r.cp} ${r.city}`;
    const rawDateStr = r.meetingDate || r.apiMeetingDate || '';
    
    // Ensure date is in YYYY-MM-DD format for HTML date input
    let dateStr = '';
    if (rawDateStr) {
      try {
        // Handle various date formats and convert to YYYY-MM-DD
        const date = new Date(rawDateStr);
        if (!isNaN(date.getTime())) {
          dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format
        }
      } catch {
        console.warn('Invalid date format for copro', r.id, ':', rawDateStr);
        dateStr = '';
      }
    }

    const previousPlanned = dateStr || null;
    const lastAg = r.lastAgDate ?? null;

    const dateInput = (
      <BlurDatePicker
        value={dateStr || null}
        onSave={(iso) => onMeetingDateChange(r.id, iso, previousPlanned, lastAg)}
        onClear={() => onMeetingDateClear(r.id, previousPlanned, lastAg)}
        minDate={moment().startOf('day')}
        errorMessageMin="La date d'AG prévue doit être dans le futur"
        showClearAction
      />
    );
    const presenceBtnClass = r.presence === 'confirmed' ? 'bg-green-600' : r.presence === 'declined' ? 'bg-red-600' : 'bg-amber-500';
    const presenceLabel = r.presence === 'confirmed' ? 'Présent' : r.presence === 'declined' ? 'Absent' : 'À confirmer';
  return {
      'N° COPRO': <span className="text-xs font-semibold text-slate-900">{r.remoteId}</span>,
      'NOM IMMEUBLE': (
        <div className="flex items-center gap-2 text-left text-xs font-semibold text-slate-800">
          <RedirectionCoproButtons coproId={r.id} />
          {r.name}
        </div>
      ),
      'ADRESSE IMMEUBLE': (
        <div className="flex items-center gap-2 text-left text-xs">
          <RedirectionMapButtons address={`${r.address} ${r.cp} ${r.city}`} />
          <span className="leading-tight whitespace-pre">{address}</span>
        </div>
      ),
      'MONTANT DES VENTES': r.salesAmount != null ? formatCurrency(r.salesAmount) : '-',
      'MONTANT COMMISSION': r.salesCommission != null ? formatCurrency(r.salesCommission) : '-',
      'NB LOTS PRINCIPAUX': r.nbLots ?? '-',
      'NB VENTE': <strong>{r.nbSales ?? '-'}</strong>,
      'M² MOY/VENTE': r.m2ForNbSales != null ? Math.round(r.m2ForNbSales) : '-',
      'PRIX MOYEN M²': r.salesAvgForM2 != null ? formatCurrency(r.salesAvgForM2) : '-',
      'DERNIÈRE AG': (
        <div className="text-left text-xs">
          <span className="hidden">{r.lastAgDate || ''}</span>
          <BlurDatePicker
            value={r.lastAgDate ?? null}
            onSave={(iso) => onLastAgDateChange(r.id, iso, previousPlanned)}
            maxDate={moment().endOf('day')}
            errorMessageMax="La date de dernière AG ne peut pas être dans le futur"
          />
        </div>
      ),
      'DATE AG PRÉVUE': (
        <div className="flex flex-col items-start gap-2 pb-2 text-left text-xs">
          {/* Hidden text for proper sorting by date */}
          <span className="hidden">{dateStr || ''}</span>
          <div className="flex items-center gap-2">
            {dateInput}
          </div>
          <div className="h-3" />
        </div>
      ),
      'GESTIONNAIRE': <span className="text-xs font-medium text-violet-600">{r.gestionnaire}</span>,
      'PRÉSENCE': (
        <button
          className={`${presenceBtnClass} rounded px-2 py-1 text-xs text-white`}
          style={{
            backgroundColor: r.presence === 'confirmed' ? '#16a34a' : r.presence === 'declined' ? '#dc2626' : '#f59e0b',
            backgroundImage: 'none',
          }}
          onClick={() => onOpenPresence(r)}
          disabled={!isEditable(r)}
        >
          {presenceLabel}
        </button>
      ),
      'ACTIONS': (
        <button
          className="rounded bg-indigo-600 px-2 py-1 text-xs text-white hover:bg-indigo-700"
          onClick={() => navigate(ROUTES.COMMUNICATION_REFERENT_CAMPAIGNS)}
        >
          Éditer Document AG
        </button>
      ),
    };
  });

  // Row coloring based on NB VENTE magnitude
  const customRowClass = (row: Record<string, unknown>) => {
    const nbVenteCell = row['NB VENTE'];
    const nbVenteStr = nbVenteCell && typeof nbVenteCell === 'object' && nbVenteCell !== null && 'props' in nbVenteCell
      ? (nbVenteCell as React.ReactElement).props.children
      : nbVenteCell;
    const nbVente = typeof nbVenteStr === 'string' ? parseInt(nbVenteStr, 10) : (typeof nbVenteStr === 'number' ? nbVenteStr : 0);
    if (Number.isNaN(nbVente)) {return '';}
    if (nbVente >= 10) {return 'bg-green-50';}
    if (nbVente >= 5) {return 'bg-green-100';}
    if (nbVente >= 3) {return 'bg-yellow-50';}
    if (nbVente >= 1) {return 'bg-yellow-full';}
    return 'bg-red-50';
  };

  return (
    <div className="data-table">
      <Table
        data={data}
        headers={headers}
        tableClass="text-center"
        sortableColumns={[
          'MONTANT DES VENTES',
          'MONTANT COMMISSION',
          'NB LOTS PRINCIPAUX',
          'NB VENTE',
          'M² MOY/VENTE',
          'PRIX MOYEN M²',
          'DERNIÈRE AG',
          'DATE AG PRÉVUE',
        ]}
        defaultSortKey="NB VENTE"
        defaultSortDirection="desc"
        customRowClass={customRowClass}
      />
    </div>
  );
};

export default GeneralAssemblyTable;


